import { useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Linkedin } from 'lucide-react';
import { useSiteSettings } from '@/hooks/use-content';
import { logAnalyticsEvent } from '@/lib/firebase';
import profilePic from '../../profileee.jpeg';

// Renders the floating dots on a <canvas> instead of animated DOM nodes.
// A CSS `transform` animation on 20 always-moving spans still registers as
// continuous, unbounded layout shift in the Layout Instability API — a canvas's
// pixels can repaint every frame without the element itself ever moving.
const HeroParticles = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let width = 0;
    let height = 0;

    const resize = () => {
      const parent = canvas.parentElement;
      width = parent?.clientWidth ?? window.innerWidth;
      height = parent?.clientHeight ?? window.innerHeight;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    const observer = new ResizeObserver(resize);
    if (canvas.parentElement) observer.observe(canvas.parentElement);

    const particles = Array.from({ length: 20 }, () => ({
      x: Math.random(),
      y: 1 + Math.random() * 0.3,
      size: 2.5 + Math.random() * 2.5,
      speed: 0.00007 + Math.random() * 0.00012,
    }));

    let rafId: number;
    let last = performance.now();

    const draw = (now: number) => {
      const dt = now - last;
      last = now;
      ctx.clearRect(0, 0, width, height);
      for (const p of particles) {
        p.y -= p.speed * dt;
        if (p.y < -0.05) {
          p.y = 1 + Math.random() * 0.3;
          p.x = Math.random();
        }
        const progress = (1.3 - p.y) / 1.35;
        const opacity = Math.max(0, 0.6 * (1 - progress));
        ctx.beginPath();
        ctx.fillStyle = `hsl(142 71% 55% / ${opacity})`;
        ctx.arc(p.x * width, p.y * height, p.size, 0, Math.PI * 2);
        ctx.fill();
      }
      rafId = requestAnimationFrame(draw);
    };
    rafId = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(rafId);
      observer.disconnect();
    };
  }, []);

  return <canvas ref={canvasRef} className="absolute inset-0 -z-10 pointer-events-none" aria-hidden="true" />;
};

const Hero = () => {
  const { t, i18n } = useTranslation();
  const { data: settings, isPending: settingsPending } = useSiteSettings();
  // Hold off on the bundled photo until settings arrive — otherwise it flashes
  // before being swapped for the one uploaded through the admin panel.
  const profileSrc = settingsPending ? undefined : settings?.profile_image_url || profilePic;
  const roles = t('hero.roles', { returnObjects: true }) as string[];
  const [text, setText] = useState("");
  const [roleIndex, setRoleIndex] = useState(0);
  const [charIndex, setCharIndex] = useState(0);
  const [deleting, setDeleting] = useState(false);

  // Reset to the first role whenever the language (and therefore the roles
  // list itself) changes, so a stale index from a longer/shorter list in the
  // previous language can't read past the end of the new one.
  useEffect(() => {
    setText('');
    setCharIndex(0);
    setDeleting(false);
    setRoleIndex(0);
  }, [i18n.language]);

  // Typing Effect
  useEffect(() => {
    const current = roles[roleIndex] ?? '';
    const speed = deleting ? 40 : 80;

    const timer = setTimeout(() => {
      if (!deleting && charIndex < current.length) {
        setText(current.slice(0, charIndex + 1));
        setCharIndex(charIndex + 1);
      } else if (deleting && charIndex > 0) {
        setText(current.slice(0, charIndex - 1));
        setCharIndex(charIndex - 1);
      } else {
        if (!deleting) {
          setTimeout(() => setDeleting(true), 600);
        } else {
          setDeleting(false);
          setRoleIndex((roleIndex + 1) % roles.length);
        }
      }
    }, speed);

    return () => clearTimeout(timer);
    // i18n.language is included (roles isn't, deliberately — t() doesn't
    // guarantee a stable array reference across renders, and depending on
    // it directly would re-trigger this effect every render): the reset
    // effect above sets roleIndex/charIndex/deleting back to 0/0/false on a
    // language change, but if they already happened to hold those exact
    // values, those setters are no-ops that wouldn't otherwise re-run this
    // effect — leaving it closed over the previous language's `roles` array.
  }, [charIndex, deleting, roleIndex, i18n.language]);

  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center justify-center pt-20 overflow-hidden"
    >
      <HeroParticles />

      <div className="container mx-auto px-4 text-center">
        <div className="mx-auto mb-8 w-36 h-36 md:w-48 md:h-48 rounded-full p-[3px] bg-gradient-primary shadow-soft">
          {profileSrc ? (
            <img
              src={profileSrc}
              alt="Ahmad Abid"
              fetchPriority="high"
              decoding="async"
              className="w-full h-full rounded-full object-cover border-2 border-background"
            />
          ) : (
            <div className="w-full h-full rounded-full bg-muted border-2 border-background" />
          )}
        </div>

        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight tracking-tight mb-6">
          {t('hero.greeting')}{" "}
          <span className="animated-name">
            Ahmad Abid
          </span>
        </h1>

        {/* Typing Text with role description */}
        <h2 className="text-xl md:text-2xl text-muted-foreground h-8 mb-8">
          {text}
          <span className="blinking-cursor">|</span>
        </h2>

        <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-10">
          <Trans i18nKey="hero.bio" components={{ b: <strong /> }} />
        </p>

        <div className="flex flex-wrap gap-4 justify-center">
          <Button
            size="lg"
            className="bg-gradient-primary hover:scale-[1.03] transition-all shadow-lg"
            onClick={() =>
              document
                .getElementById("projects")
                ?.scrollIntoView({ behavior: "smooth" })
            }
            aria-label={t('hero.viewProjectsAria')}
          >
            {t('hero.viewProjects')}
          </Button>

          <Button
            size="lg"
            variant="outline"
            className="hover:bg-primary/10 hover:border-primary/40 transition-all"
            onClick={() =>
              window.open(
                "https://www.linkedin.com/in/ahmad-abid-1a57a3227/",
                "_blank"
              )
            }
            aria-label={t('hero.connectAria')}
          >
            <Linkedin className="mr-2 h-4 w-4" />
            {t('hero.connect')}
          </Button>

          <Button
            size="lg"
            className="bg-gradient-primary hover:scale-[1.03] transition-all shadow-lg"
            onClick={() => {
              logAnalyticsEvent('resume_download');
              const link = document.createElement("a");
              link.href = settings?.resume_url || "/Resume.pdf";
              link.download = "My_Resume.pdf";
              link.click();
            }}
            aria-label={t('hero.downloadResumeAria')}
          >
            {t('hero.downloadResume')}
          </Button>
        </div>
      </div>
    </section>
  );
};

export default Hero;
